import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  IconButton,
  Paper,
  Popover,
  PopoverProps,
  Typography
} from '@material-ui/core';
import {
  Add as AddIcon,
  Description as DescriptionIcon,
  MoreVert as MoreVertIcon,
  Update as UpdateIcon
} from '@material-ui/icons';
import { Alert } from '@material-ui/lab';
import { AppSnackbar } from '@sierralabs/react-material-ui';
import { Workflow } from '@sierralabs/workflow';

import { ApiParams } from '../../../common/api/api';
import { FetchApiCallback } from '../../../common/api/Api.hooks';
import { PaginatedList } from '../../../common/PaginatedList.interface';
import BoardColumnMenu from '../../tasks/board/BoardColumnMenu';
import { TaskEntity } from '../../tasks/Task.interface';
import { useFindWorkflowVersionByEntity } from '../../workflow/Workflow.hooks';
import {
  WorkflowEntity,
  WorkflowStatus
} from '../../workflow/Workflow.interface';
import { getProductEntityName } from '../Product.config';
import {
  getProductEntityPrefix,
  useFindFilteredProductDesignInputs,
  useFindFilteredProductDesignOutputs,
  useFindFilteredProductDesignSpecs,
  useFindFilteredProductUserNeeds,
  useFindFilteredProductValidations,
  useFindFilteredProductVerifications,
  useProductEntityState,
  useProductViewParams
} from '../Product.hooks';
import {
  Product,
  ProductDesignInput,
  ProductDesignOutput,
  ProductDesignSpec,
  ProductEntity,
  ProductExportEntity,
  ProductUserNeed,
  ProductValidation,
  ProductVerification
} from '../Product.interface';
import { ProductBoardContext } from './ProductBoard.context';
import ProductBoardDesignInput from './ProductBoardDesignInput';
import ProductBoardDesignOutput from './ProductBoardDesignOutput';
import ProductBoardDesignSpec from './ProductBoardDesignSpec';
import ProductBoardValidation from './ProductBoardDesignValidation';
import ProductBoardVerification from './ProductBoardDesignVerification';
import ProductBoardUserNeed from './ProductBoardUserNeed';

const StyledLoadMoreContainer = styled(Box)`
  .icon {
    display: flex;
    margin-right: 8px;
  }
`;

export enum ProductBoardColumnEntity {
  UserNeed = 'UserNeed',
  DesignInput = 'DesignInput',
  DesignOutput = 'DesignOutput',
  Verification = 'Verification',
  Validation = 'Validation',
  DesignSpec = 'DesignSpec'
}

export const ProductBoardColumnEntityURL: {
  [key in ProductBoardColumnEntity]: string;
} = {
  [ProductBoardColumnEntity.UserNeed]: 'user-needs',
  [ProductBoardColumnEntity.DesignInput]: 'design-inputs',
  [ProductBoardColumnEntity.DesignOutput]: 'design-outputs',
  [ProductBoardColumnEntity.Verification]: 'verifications',
  [ProductBoardColumnEntity.Validation]: 'validations',
  [ProductBoardColumnEntity.DesignSpec]: 'design-specs'
};

export const getColumnTitle = (entity: ProductBoardColumnEntity) => {
  switch (entity) {
    case ProductBoardColumnEntity.UserNeed:
      return getProductEntityName('userNeed');
    case ProductBoardColumnEntity.DesignInput:
      return getProductEntityName('designInput');
    case ProductBoardColumnEntity.DesignOutput:
      return getProductEntityName('designOutput');
    case ProductBoardColumnEntity.Verification:
      return getProductEntityName('verification');
    case ProductBoardColumnEntity.Validation:
      return getProductEntityName('validation');
    case ProductBoardColumnEntity.DesignSpec:
      return getProductEntityName('designSpec');
  }
};

export function convertToProductExportEntity(
  entity: ProductBoardColumnEntity
): ProductExportEntity {
  switch (entity) {
    case ProductBoardColumnEntity.UserNeed:
      return ProductExportEntity.UserNeed;
    case ProductBoardColumnEntity.DesignInput:
      return ProductExportEntity.DesignInput;
    case ProductBoardColumnEntity.DesignOutput:
      return ProductExportEntity.DesignOutput;
    case ProductBoardColumnEntity.Verification:
      return ProductExportEntity.Verification;
    case ProductBoardColumnEntity.Validation:
      return ProductExportEntity.Validation;
    case ProductBoardColumnEntity.DesignSpec:
      return ProductExportEntity.DesignSpec;
  }
}

export function convertToWorkflowEntity(
  entity: ProductBoardColumnEntity
): WorkflowEntity {
  switch (entity) {
    case ProductBoardColumnEntity.UserNeed:
      return WorkflowEntity.UserNeed;
    case ProductBoardColumnEntity.DesignInput:
      return WorkflowEntity.DesignInput;
    case ProductBoardColumnEntity.DesignOutput:
      return WorkflowEntity.DesignOutput;
    case ProductBoardColumnEntity.Verification:
      return WorkflowEntity.DesignVerification;
    case ProductBoardColumnEntity.Validation:
      return WorkflowEntity.DesignValidation;
    case ProductBoardColumnEntity.DesignSpec:
      return WorkflowEntity.DesignSpec;
  }
}

export interface ProductBoardColumnProps {
  product: Product;
  entity: ProductBoardColumnEntity;
  apiParams?: ApiParams;
  collapsed?: boolean;
  hideHeader?: boolean;
  userNeeds?: Partial<ProductUserNeed>[];
  designInputs?: Partial<ProductDesignInput>[];
  designOutputs?: Partial<ProductDesignOutput>[];
  verifications?: Partial<ProductVerification>[];
  validations?: Partial<ProductValidation>[];
  designSpecs?: Partial<ProductDesignSpec>[];
  // overrides default fetch
  handleFetch?: () => void;
}

const ProductBoardColumn: React.FC<ProductBoardColumnProps> = (
  props: ProductBoardColumnProps
) => {
  const {
    product,
    entity,
    apiParams: apiParamsProp,
    collapsed = false,
    hideHeader = false,
    // controlled values
    userNeeds,
    designInputs,
    designOutputs,
    verifications,
    validations,
    designSpecs,
    handleFetch
  } = props;
  const limit: number = 100;
  const concatRef = useRef<boolean>(false);
  const [page, setPage] = useState<number>(0);
  const [moreMenu, setMoreMenu] = useState<HTMLButtonElement>();
  const [popoverProps, setPopoverProps] = useState<Partial<PopoverProps>>({});
  const [apiParams, setApiParams] = useState<ApiParams | undefined>(
    apiParamsProp
  );
  const query: string = useProductViewParams(true);
  const columnTitle = useMemo(() => getColumnTitle(entity) as string, [entity]);
  const {
    selected,
    entity: selectedEntity,
    removeSelected
  } = useContext(ProductBoardContext);
  const workflowEntity: WorkflowEntity = useMemo(() => {
    return convertToWorkflowEntity(entity);
  }, [entity]);
  const { workflowVersion, loadingError, findWorkflowVersion } =
    useFindWorkflowVersionByEntity(workflowEntity);
  const workflow: Workflow | undefined = useMemo(() => {
    return workflowVersion?.config && new Workflow(workflowVersion.config);
  }, [workflowVersion]);
  const {
    productUserNeedList,
    productUserNeedPaginated,
    isLoadingProductUserNeeds,
    findProductUserNeeds
  } = useFindFilteredProductUserNeeds(product.id, selected?.id, selectedEntity);
  const {
    productDesignInputList,
    productDesignInputPaginated,
    isLoadingProductDesignInputs,
    findProductDesignInputs
  } = useFindFilteredProductDesignInputs(
    product.id,
    selected?.id,
    selectedEntity
  );
  const {
    productDesignOutputList,
    productDesignOutputPaginated,
    isLoadingProductDesignOutputs,
    findProductDesignOutputs
  } = useFindFilteredProductDesignOutputs(
    product.id,
    selected?.id,
    selectedEntity
  );
  const {
    productVerificationList,
    productVerificationPaginated,
    isLoadingProductVerifications,
    findProductVerifications
  } = useFindFilteredProductVerifications(
    product.id,
    selected?.id,
    selectedEntity
  );
  const {
    productValidationList,
    productValidationPaginated,
    isLoadingProductValidations,
    findProductValidations
  } = useFindFilteredProductValidations(
    product.id,
    selected?.id,
    selectedEntity
  );
  const {
    productDesignSpecList,
    productDesignSpecPaginated,
    isLoadingProductDesignSpecs,
    findProductDesignSpecs
  } = useFindFilteredProductDesignSpecs(
    product.id,
    selected?.id,
    selectedEntity
  );
  const [productUserNeeds, setProductUserNeeds] = useProductEntityState<
    Partial<ProductUserNeed>
  >(userNeeds || []);
  const [productDesignInputs, setProductDesignInputs] = useProductEntityState<
    Partial<ProductDesignInput>
  >(designInputs || []);
  const [productDesignOutputs, setProductDesignOutputs] = useProductEntityState<
    Partial<ProductDesignOutput>
  >(designOutputs || []);
  const [productVerifications, setProductVerifications] = useProductEntityState<
    Partial<ProductVerification>
  >(verifications || []);
  const [productValidations, setProductValidations] = useProductEntityState<
    Partial<ProductValidation>
  >(validations || []);
  const [productDesignSpecs, setProductDesignSpecs] = useProductEntityState<
    Partial<ProductDesignSpec>
  >(designSpecs || []);

  const isLoading: boolean =
    isLoadingProductUserNeeds ||
    isLoadingProductDesignInputs ||
    isLoadingProductDesignOutputs ||
    isLoadingProductVerifications ||
    isLoadingProductValidations ||
    isLoadingProductDesignSpecs;

  const paginatedList: PaginatedList<ProductEntity> | undefined =
    useMemo(() => {
      switch (entity) {
        case ProductBoardColumnEntity.UserNeed:
          return productUserNeedPaginated;
        case ProductBoardColumnEntity.DesignInput:
          return productDesignInputPaginated;
        case ProductBoardColumnEntity.DesignOutput:
          return productDesignOutputPaginated;
        case ProductBoardColumnEntity.Verification:
          return productVerificationPaginated;
        case ProductBoardColumnEntity.Validation:
          return productValidationPaginated;
        case ProductBoardColumnEntity.DesignSpec:
          return productDesignSpecPaginated;
      }
    }, [
      entity,
      productUserNeedPaginated,
      productDesignInputPaginated,
      productDesignOutputPaginated,
      productVerificationPaginated,
      productValidationPaginated,
      productDesignSpecPaginated
    ]);

  const isCurrentEntity: boolean = useMemo(() => {
    return !selectedEntity || entity === selectedEntity;
  }, [entity, selectedEntity]);

  const params: ApiParams = useMemo(() => {
    const params: ApiParams = { ...apiParams };
    params.limit = limit;
    // update params only if selectedEntity does not match
    if (isCurrentEntity) {
      params.page = page;
    }
    return params;
  }, [page, limit, apiParams, isCurrentEntity]);

  const findProductEntities: FetchApiCallback = useMemo(() => {
    // only trigger fetch if the callback for the entity changes
    switch (entity) {
      case ProductBoardColumnEntity.UserNeed:
        return findProductUserNeeds;
      case ProductBoardColumnEntity.DesignInput:
        return findProductDesignInputs;
      case ProductBoardColumnEntity.DesignOutput:
        return findProductDesignOutputs;
      case ProductBoardColumnEntity.Verification:
        return findProductVerifications;
      case ProductBoardColumnEntity.Validation:
        return findProductValidations;
      case ProductBoardColumnEntity.DesignSpec:
        return findProductDesignSpecs;
    }
  }, [
    entity,
    findProductUserNeeds,
    findProductDesignInputs,
    findProductDesignOutputs,
    findProductVerifications,
    findProductValidations,
    findProductDesignSpecs
  ]);

  const fetchProductData = useCallback(() => {
    handleFetch ? handleFetch() : findProductEntities(params);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, findProductEntities]);

  const removeItem = (
    index: number = 0,
    entities: Partial<ProductEntity>[]
  ): Partial<ProductEntity>[] => {
    entities = [...entities];
    entities.splice(index, 1);
    return entities;
  };

  const onSave = (_: ProductEntity, created: boolean = false) => {
    // only fetch if item was created
    if (!created) {
      return;
    }
    // reload from first page to show added item with proper pagination
    concatRef.current = false;
    if (page !== 0) {
      setPage(0);
    } else {
      fetchProductData();
    }
  };

  // remove item without id or title
  const onError = (
    value: Partial<ProductEntity>,
    index: number,
    entity: ProductBoardColumnEntity
  ) => {
    if (value.id && value.title) {
      return;
    }
    switch (entity) {
      case ProductBoardColumnEntity.UserNeed:
        setProductUserNeeds(removeItem(index, productUserNeeds));
        break;
      case ProductBoardColumnEntity.DesignInput:
        setProductDesignInputs(removeItem(index, productDesignInputs));
        break;
      case ProductBoardColumnEntity.DesignOutput:
        setProductDesignOutputs(removeItem(index, productDesignOutputs));
        break;
      case ProductBoardColumnEntity.Verification:
        setProductVerifications(removeItem(index, productVerifications));
        break;
      case ProductBoardColumnEntity.Validation:
        setProductValidations(removeItem(index, productValidations));
        break;
      case ProductBoardColumnEntity.DesignSpec:
        setProductDesignSpecs(removeItem(index, productDesignSpecs));
        break;
    }
  };

  const createDefaultProduct = (): Partial<ProductEntity> => {
    // index 0 is the root stateNode path
    const path: string | undefined = workflow?.getAllPaths()[1];
    return {
      title: '',
      // runtime task to render status label
      task: {
        name: '',
        type: workflowEntity,
        version: 1,
        status: path || '',
        created: new Date()
        // workflow: workflowVersion && {
        //   id: workflowVersion.workflowId,
        //   name: workflowVersion.name,
        //   entity: workflowVersion.entity,
        //   version: workflowVersion.version,
        //   status: WorkflowStatus.Published
        // }
      }
    };
  };

  useEffect(() => {
    fetchProductData();
  }, [fetchProductData]);

  useEffect(() => {
    findWorkflowVersion();
  }, [findWorkflowVersion]);

  useEffect(() => {
    // reset page if apiParams (filters) changed
    setApiParams(apiParamsProp);
    setPage(0);
  }, [apiParamsProp]);

  useEffect(() => {
    // reset page if no pagination exists
    !paginatedList && setPage(0);
  }, [paginatedList]);

  useEffect(() => {
    if (userNeeds || productUserNeedList) {
      setProductUserNeeds(
        productUserNeedList || userNeeds || [],
        concatRef.current
      );
      concatRef.current = false;
    }
  }, [userNeeds, productUserNeedList]);
  useEffect(() => {
    console.log('productDesignInputList', productDesignInputList);
    if (designInputs || productDesignInputList) {
      setProductDesignInputs(
        productDesignInputList || designInputs || [],
        concatRef.current
      );
      concatRef.current = false;
    }
  }, [designInputs, productDesignInputList]);
  useEffect(() => {
    if (designOutputs || productDesignOutputList) {
      setProductDesignOutputs(
        productDesignOutputList || designOutputs || [],
        concatRef.current
      );
      concatRef.current = false;
    }
  }, [designOutputs, productDesignOutputList]);
  useEffect(() => {
    if (verifications || productVerificationList) {
      setProductVerifications(
        productVerificationList || verifications || [],
        concatRef.current
      );
      concatRef.current = false;
    }
  }, [verifications, productVerificationList]);
  useEffect(() => {
    if (validations || productValidationList) {
      setProductValidations(
        productValidationList || validations || [],
        concatRef.current
      );
      concatRef.current = false;
    }
  }, [validations, productValidationList]);
  useEffect(() => {
    if (designSpecs || productDesignSpecList) {
      setProductDesignSpecs(
        productDesignSpecList || designSpecs || [],
        concatRef.current
      );
      concatRef.current = false;
    }
  }, [designSpecs, productDesignSpecList]);

  return (
    <Paper elevation={0} className='column'>
      {!hideHeader && (
        <div className='column-header'>
          <DescriptionIcon />
          <Typography variant='overline' className='title'>
            <Link
              className='column-header-link'
              to={`/portal/products/${product.id}/${ProductBoardColumnEntityURL[entity]}${query}`}
            >
              {columnTitle}
            </Link>
          </Typography>
          <div className='buttons'>
            <IconButton
              size='small'
              onClick={() => {
                const value: Partial<ProductEntity> = createDefaultProduct();
                switch (entity) {
                  case ProductBoardColumnEntity.UserNeed:
                    setProductUserNeeds([value, ...productUserNeeds]);
                    break;
                  case ProductBoardColumnEntity.DesignInput:
                    setProductDesignInputs([value, ...productDesignInputs]);
                    break;
                  case ProductBoardColumnEntity.DesignOutput:
                    setProductDesignOutputs([value, ...productDesignOutputs]);
                    break;
                  case ProductBoardColumnEntity.Verification:
                    setProductVerifications([value, ...productVerifications]);
                    break;
                  case ProductBoardColumnEntity.Validation:
                    setProductValidations([value, ...productValidations]);
                    break;
                  case ProductBoardColumnEntity.DesignSpec:
                    setProductDesignSpecs([value, ...productDesignSpecs]);
                    break;
                }
              }}
            >
              <AddIcon />
            </IconButton>
            <IconButton
              size='small'
              onClick={event => setMoreMenu(event.currentTarget)}
            >
              <MoreVertIcon />
            </IconButton>
            {moreMenu &&
              (() => {
                let taskEntities: Partial<TaskEntity>[];
                switch (entity) {
                  case ProductBoardColumnEntity.UserNeed:
                    taskEntities = productUserNeeds;
                    break;
                  case ProductBoardColumnEntity.DesignInput:
                    taskEntities = productDesignInputs;
                    break;
                  case ProductBoardColumnEntity.DesignOutput:
                    taskEntities = productDesignOutputs;
                    break;
                  case ProductBoardColumnEntity.Verification:
                    taskEntities = productVerifications;
                    break;
                  case ProductBoardColumnEntity.Validation:
                    taskEntities = productValidations;
                    break;
                  case ProductBoardColumnEntity.DesignSpec:
                    taskEntities = productDesignSpecs;
                    break;
                }
                return (
                  <Popover
                    open
                    anchorEl={moreMenu}
                    onClose={() => setMoreMenu(undefined)}
                    {...popoverProps}
                  >
                    <BoardColumnMenu
                      taskEntities={taskEntities as any}
                      onRender={() => {
                        setPopoverProps({
                          anchorOrigin: {
                            vertical: 'bottom',
                            horizontal: 'right'
                          },
                          transformOrigin: {
                            vertical: 'top',
                            horizontal: 'right'
                          }
                        });
                      }}
                      onChange={() => {
                        setMoreMenu(undefined);
                        fetchProductData();
                      }}
                    />
                  </Popover>
                );
              })()}
          </div>
        </div>
      )}

      <Collapse in={!hideHeader && entity === selectedEntity}>
        <Alert
          icon={false}
          severity='warning'
          action={
            <Button
              size='small'
              variant='text'
              color='inherit'
              onClick={removeSelected}
            >
              Deselect
            </Button>
          }
        >
          Filter enabled
        </Alert>
      </Collapse>

      <Paper elevation={1} className='card-container'>
        {(() => {
          switch (entity) {
            case ProductBoardColumnEntity.UserNeed:
              return productUserNeeds.map((userNeed, index) => (
                <ProductBoardUserNeed
                  product={product}
                  key={`${userNeed.id}_${index}`}
                  userNeed={userNeed}
                  collapsed={collapsed}
                  onSave={onSave}
                  onError={() => {
                    onError(userNeed, index, ProductBoardColumnEntity.UserNeed);
                  }}
                />
              ));
            case ProductBoardColumnEntity.DesignInput:
              return productDesignInputs.map((designInput, index) => (
                <ProductBoardDesignInput
                  product={product}
                  key={`${designInput.id}_${index}`}
                  designInput={designInput}
                  collapsed={collapsed}
                  onSave={onSave}
                  onError={() => {
                    onError(
                      designInput,
                      index,
                      ProductBoardColumnEntity.DesignInput
                    );
                  }}
                />
              ));
            case ProductBoardColumnEntity.DesignOutput:
              return productDesignOutputs.map((designOutput, index) => (
                <ProductBoardDesignOutput
                  product={product}
                  key={`${designOutput.id}_${index}`}
                  designOutput={designOutput}
                  collapsed={collapsed}
                  onSave={onSave}
                  onError={() => {
                    onError(
                      designOutput,
                      index,
                      ProductBoardColumnEntity.DesignOutput
                    );
                  }}
                />
              ));
            case ProductBoardColumnEntity.Verification:
              return productVerifications.map((verification, index) => (
                <ProductBoardVerification
                  product={product}
                  key={`${verification.id}_${index}`}
                  verification={verification}
                  collapsed={collapsed}
                  onSave={onSave}
                  onError={() => {
                    onError(
                      verification,
                      index,
                      ProductBoardColumnEntity.Verification
                    );
                  }}
                />
              ));
            case ProductBoardColumnEntity.Validation:
              return productValidations.map((validation, index) => (
                <ProductBoardValidation
                  product={product}
                  key={`${validation.id}_${index}`}
                  validation={validation}
                  collapsed={collapsed}
                  onSave={onSave}
                  onError={() => {
                    onError(
                      validation,
                      index,
                      ProductBoardColumnEntity.Validation
                    );
                  }}
                />
              ));
            case ProductBoardColumnEntity.DesignSpec:
              return productDesignSpecs.map((designSpec, index) => (
                <ProductBoardDesignSpec
                  product={product}
                  key={`${designSpec.id}_${index}`}
                  designSpec={designSpec}
                  collapsed={collapsed}
                  onSave={onSave}
                  onError={() => {
                    onError(
                      designSpec,
                      index,
                      ProductBoardColumnEntity.DesignSpec
                    );
                  }}
                />
              ));
          }
        })()}

        {paginatedList && paginatedList.total > (page + 1) * limit && (
          <StyledLoadMoreContainer
            mb={1}
            display='flex'
            justifyContent='center'
          >
            <Button
              size='small'
              color='primary'
              disabled={isLoading}
              onClick={() => {
                concatRef.current = true;
                setPage(page + 1);
              }}
            >
              <span className='icon'>
                {isLoading ? <CircularProgress size={20} /> : <UpdateIcon />}
              </span>
              Load More
            </Button>
          </StyledLoadMoreContainer>
        )}
      </Paper>
      {loadingError && (
        <AppSnackbar open={Boolean(loadingError)} severity='error'>
          Error finding a published workflow for '{entity}'
        </AppSnackbar>
      )}
    </Paper>
  );
};

export default ProductBoardColumn;
